import { createFileRoute, Link } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { useSettings } from '@/hooks'
import { useKlaviyoStatus } from '@/hooks/use-klaviyo'
import { useTinyStatus } from '@/hooks/use-tiny'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronRight, CheckCircle2, Circle } from 'lucide-react'
import { PageSkeleton } from '@/components/shared/page-skeleton'

export const Route = createFileRoute('/_app/settings/integrations/')({
  component: IntegrationsPage,
})

// Logos como componentes SVG inline para evitar assets externos
function GuruLogo({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-purple-800 text-white font-bold ${className}`}
    >
      G
    </div>
  )
}

function YampiLogo({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold ${className}`}
    >
      Y
    </div>
  )
}

function KlaviyoLogo({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded-lg bg-[#1A1A2E] text-green-400 font-bold ${className}`}
    >
      K
    </div>
  )
}

function TinyLogo({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded-lg bg-linear-to-br from-orange-500 to-orange-700 text-white font-bold ${className}`}
    >
      T
    </div>
  )
}

interface Integration {
  id: string
  name: string
  description: string
  href: string
  logo: React.ComponentType<{ className?: string }>
  checkEnabled: (
    settings: { hasGuruToken: boolean; hasYampiToken: boolean },
    klaviyoEnabled?: boolean,
    tinyEnabled?: boolean
  ) => boolean
}

const INTEGRATIONS: Integration[] = [
  {
    id: 'guru',
    name: 'Guru',
    description: 'Receba vendas e processe comissões automaticamente.',
    href: '/settings/integrations/guru',
    logo: GuruLogo,
    checkEnabled: (s) => s.hasGuruToken,
  },
  {
    id: 'yampi',
    name: 'Yampi',
    description: 'Crie cupons e rastreie pedidos do seu e-commerce.',
    href: '/settings/integrations/yampi',
    logo: YampiLogo,
    checkEnabled: (s) => s.hasYampiToken,
  },
  {
    id: 'klaviyo',
    name: 'Klaviyo',
    description: 'Dispare e-mails automáticos para seus indicadores.',
    href: '/settings/integrations/klaviyo',
    logo: KlaviyoLogo,
    checkEnabled: (_, klaviyoEnabled) => klaviyoEnabled ?? false,
  },
  {
    id: 'tiny',
    name: 'Tiny ERP',
    description: 'Gerencie envios de bonificação e pedidos no Tiny.',
    href: '/settings/integrations/tiny',
    logo: TinyLogo,
    checkEnabled: (_s, _k, tinyEnabled) => tinyEnabled ?? false,
  },
]

function IntegrationsPage() {
  const { data: settings, isLoading } = useSettings()
  const { data: klaviyoStatus } = useKlaviyoStatus()
  const { data: tinyStatus } = useTinyStatus()

  if (isLoading) {
    return <PageSkeleton sections={1} />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        size="sm"
        title="Integrações"
        description="Conecte sua loja com as ferramentas que você já usa."
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {INTEGRATIONS.map((integration) => {
          const isEnabled = settings
            ? integration.checkEnabled(settings, klaviyoStatus?.enabled, tinyStatus?.enabled)
            : false
          const Logo = integration.logo

          return (
            <Link key={integration.id} to={integration.href} className="group block h-full">
              <Card
                className={`h-full transition-all duration-200 hover:shadow-md ${isEnabled ? 'border-green-200 dark:border-green-900/50' : ''} group-hover:border-primary/50`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <Logo className="h-10 w-10 text-sm shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base">{integration.name}</CardTitle>
                        {isEnabled ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                        )}
                      </div>
                      <CardDescription className="text-xs mt-1 line-clamp-2">
                        {integration.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-medium ${isEnabled ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}
                    >
                      {isEnabled ? 'Conectado' : 'Não configurado'}
                    </span>
                    <div className="flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      Configurar <ChevronRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
